import { useState, useEffect, useRef, useCallback } from 'react';

// Custom smooth scroll with easeInOutQuart — much smoother than browser native
const smoothScrollTo = (element, to, duration = 380) => {
    if (!element) return;
    const start = element.scrollTop;
    const change = to - start;
    if (Math.abs(change) < 2) return;
    const startTime = performance.now();
    const ease = (t) => t < 0.5 ? 8*t*t*t*t : 1 - Math.pow(-2*t + 2, 4) / 2;
    const animate = (now) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        element.scrollTop = start + change * ease(progress);
        if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
};
import { BookOpen, HelpCircle, ChevronLeft, ChevronRight, Loader2, CheckCircle, Circle, Clock, Trophy, AlertTriangle, Info, Sparkles, Settings, Sun, Moon } from 'lucide-react';
import courseService from '../../../services/courseService';
import { detectMediaType } from '../../../utils/mediaUtils';
import { useCourseTimer } from '../../../hooks/useCourseTimer';
import { useLanguage } from '../../../context/LanguageContext';
import { useTheme } from '../../../context/ThemeContext';
import { useHelp } from '../../../context/HelpContext';
import { useAuth } from '../../../context/AuthContext';
import useAudioManager from '../../../hooks/useAudioManager';
import AudioPlayButton from './AudioPlayButton';
import CourseSettingsModal from './CourseSettingsModal';
import CourseAudioBottomBar from './CourseAudioBottomBar';
import CourseSectionsSidebar from './CourseSectionsSidebar';

const CourseModal = ({ course, moduleId, onClose, language = 'fr' }) => {
    const { setLanguage } = useLanguage();
    const { theme, setTheme } = useTheme();
    const { startTour, hasSeenPage, helpPrefs, isPrefsSynced, registerOverlay, unregisterOverlay, setActiveHelpPage } = useHelp();
    const { user } = useAuth();
    const initialAudioPrefs = user?.dashboardPrefs?.audioPrefs || null;

    // Register this overlay so background page tours are blocked + declare active help page
    // so the floating Guide button triggers the course-modal tour (not the URL-based fallback).
    useEffect(() => {
        registerOverlay('course-modal');
        setActiveHelpPage('course-modal');
        return () => {
            unregisterOverlay('course-modal');
            setActiveHelpPage(null);
        };
    }, [registerOverlay, unregisterOverlay, setActiveHelpPage]);

    const [courseDetail, setCourseDetail] = useState(null);
    const [chapters, setChapters] = useState([]);
    const [currentChapterIndex, setCurrentChapterIndex] = useState(0);
    const [paragraphs, setParagraphs] = useState([]);
    const [paragraphsLoading, setParagraphsLoading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Audio player state
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [audioDuration, setAudioDuration] = useState(0);

    // Session and progress state
    const [sessionId, setSessionId] = useState(null);
    const [validatedSections, setValidatedSections] = useState({});       // Global: union of all history + session
    const [sessionValidatedSections, setSessionValidatedSections] = useState({}); // Current session only
    const [validating, setValidating] = useState(false);
    const [completing, setCompleting] = useState(false);
    const [showCompleteWarning, setShowCompleteWarning] = useState(false);
    const [showLastChapterConfirm, setShowLastChapterConfirm] = useState(false);

    const scrollContainerRef = useRef(null);

    // Audio settings modal
    const [showSettings, setShowSettings] = useState(false);

    // Pills slider
    const pillsContainerRef = useRef(null);
    const [pillsCanScrollLeft, setPillsCanScrollLeft] = useState(false);
    const [pillsCanScrollRight, setPillsCanScrollRight] = useState(false);

    // Course timer
    const handleTimerUpdate = useCallback((sid, time) => {
        courseService.updateSession(sid, time).catch(err => console.warn('Timer save failed:', err));
    }, []);
    const timer = useCourseTimer(sessionId, handleTimerUpdate);

    // Paragraph audio manager
    const audioAutoAdvancedRef = useRef(false);
    const audioManager = useAudioManager({
        initialAudioPrefs,
        onChapterEnd: () => {
            audioAutoAdvancedRef.current = true;
            handleValidateSection();
        }
    });

    // Auto-pause after 60s of inactivity (disabled while audio is playing)
    const [coursePaused, setCoursePaused] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const inactivityTimerRef = useRef(null);
    useEffect(() => {
        if (coursePaused || loading || audioManager.isPlaying || isPlaying) {
            if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
            return;
        }
        const INACTIVITY_DELAY = 120_000;
        const resetTimer = () => {
            if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
            inactivityTimerRef.current = setTimeout(() => {
                // Double-check audio isn't playing before pausing
                if (!audioManager.isPlaying) {
                    setCoursePaused(true);
                    timer.pause();
                }
            }, INACTIVITY_DELAY);
        };
        const events = ['pointerdown', 'keydown', 'scroll', 'touchstart'];
        events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
        resetTimer();
        return () => {
            events.forEach(e => window.removeEventListener(e, resetTimer));
            if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
        };
    }, [coursePaused, loading, audioManager.isPlaying, isPlaying]);

    const handleResumeCourse = useCallback(() => {
        setCoursePaused(false);
        timer.resume();
    }, [timer]);

    // Auto-resume when audio starts playing while paused
    useEffect(() => {
        if (coursePaused && (audioManager.isPlaying || isPlaying)) {
            handleResumeCourse();
        }
    }, [audioManager.isPlaying, isPlaying, coursePaused, handleResumeCourse]);

    useEffect(() => {
        if (course?.idCourse) {
            loadCourseContent();
        }
    }, [course?.idCourse]);

    useEffect(() => {
        if (chapters.length > 0 && chapters[currentChapterIndex]) {
            const shouldAutoPlay = audioManager.isPlaying || audioAutoAdvancedRef.current;
            audioAutoAdvancedRef.current = false;
            audioManager.cleanup();
            loadChapterContent(chapters[currentChapterIndex].idChapter, shouldAutoPlay);
        }
    }, [chapters, currentChapterIndex]);

    const loadCourseContent = async () => {
        setLoading(true);
        setError(null);
        try {
            const [detail, progress] = await Promise.all([
                courseService.getCourseDetail(course.idCourse),
                courseService.getProgress(course.idCourse).catch(() => ({ hasProgress: false }))
            ]);

            setCourseDetail(detail.course);
            setChapters(detail.chapters || []);

            if (progress.hasProgress) {
                setValidatedSections(progress.validatedSections || {});
                setSessionValidatedSections(progress.sessionValidatedSections || {});

                if (progress.sessionId) {
                    // Active session exists, resume it
                    setSessionId(progress.sessionId);
                    timer.start(progress.duration || 0);
                } else {
                    // History exists but no active session → start a fresh one
                    try {
                        const session = await courseService.startSession(course.idCourse);
                        setSessionId(session.sessionId);
                        timer.start(0);
                    } catch (sessionErr) {
                        console.warn('Could not start session:', sessionErr);
                    }
                }

                // Jump to first unvalidated section (resume where user left off)
                const sessionVal = progress.sessionValidatedSections || {};
                if (detail.chapters?.length > 0) {
                    const firstUnvalidatedIdx = detail.chapters.findIndex(
                        ch => sessionVal[String(ch.idChapter)] !== "1"
                    );
                    if (firstUnvalidatedIdx > 0) {
                        setCurrentChapterIndex(firstUnvalidatedIdx);
                    }
                }
            } else {
                setSessionValidatedSections({});
                try {
                    const session = await courseService.startSession(course.idCourse);
                    setSessionId(session.sessionId);
                    timer.start(session.duration || 0);
                } catch (sessionErr) {
                    console.warn('Could not start session:', sessionErr);
                }
            }
        } catch (err) {
            setError(err.message);
            console.error('Failed to load course content:', err);
        } finally {
            setLoading(false);
        }
    };

    const [chapterAudio, setChapterAudio] = useState(null);

    const loadChapterContent = async (chapterId, autoPlayAudio = false) => {
        setParagraphsLoading(true);
        try {
            const { paragraphs: data, chapterAudio: chapAudio } = await courseService.getParagraphs(chapterId);
            setParagraphs(data || []);
            setChapterAudio(chapAudio || null);
            audioManager.loadChapterAudios(data || [], chapAudio, language, autoPlayAudio);
        } catch (err) {
            console.error('Failed to load chapter content:', err);
            setParagraphs([]);
            setChapterAudio(null);
        } finally {
            setParagraphsLoading(false);
        }
    };

    // Reload audio list when language changes — never auto-play
    useEffect(() => {
        if (paragraphs.length > 0) {
            audioManager.stop();
            audioManager.loadChapterAudios(paragraphs, chapterAudio, language);
        }
    }, [language]);

    // Auto-trigger help tour on first course session
    useEffect(() => {
        if (!isPrefsSynced) return;
        if (helpPrefs.globalEnabled && helpPrefs.autoTrigger && !hasSeenPage('course-modal') && !loading && chapters.length > 0) {
            const timer = setTimeout(() => startTour('course-modal'), 1000);
            return () => clearTimeout(timer);
        }
    }, [isPrefsSynced, loading, chapters.length, helpPrefs.globalEnabled, helpPrefs.autoTrigger, hasSeenPage]);

    // Coexistence: pause course-level audio when paragraph audio plays, and vice-versa
    useEffect(() => {
        if (audioManager.isPlaying && audioRef.current && !audioRef.current.paused) {
            audioRef.current.pause();
            setIsPlaying(false);
        }
    }, [audioManager.isPlaying]);

    const getSectionTitle = (chapter) => {
        if (language === 'fr') {
            return chapter.libelle_fr || chapter.titreChapter || `Section ${chapters.indexOf(chapter) + 1}`;
        }
        return chapter.libelle_en || chapter.titreChapter || `Section ${chapters.indexOf(chapter) + 1}`;
    };

    const isSectionValidated = (chapterId) => {
        return validatedSections[String(chapterId)] === "1";
    };

    const handleValidateSection = () => {
        const currentChapter = chapters[currentChapterIndex];
        if (!currentChapter || !sessionId) return;

        // Check if this is the last unvalidated chapter in the session
        const unvalidatedChapters = chapters.filter(ch => sessionValidatedSections[String(ch.idChapter)] !== "1");
        if (unvalidatedChapters.length === 1 && String(unvalidatedChapters[0].idChapter) === String(currentChapter.idChapter)) {
            setShowLastChapterConfirm(true);
            return;
        }

        doValidateSection();
    };

    const doValidateSection = async () => {
        const currentChapter = chapters[currentChapterIndex];
        if (!currentChapter || !sessionId) return;

        setValidating(true);
        try {
            const result = await courseService.validateSection(sessionId, currentChapter.idChapter);
            setValidatedSections(prev => ({ ...prev, ...result.validatedSections }));
            const newSessionValidated = { ...sessionValidatedSections, [String(currentChapter.idChapter)]: "1" };
            setSessionValidatedSections(newSessionValidated);

            // Auto-complete when all chapters validated in this session
            const allDoneInSession = chapters.every(ch => newSessionValidated[String(ch.idChapter)] === "1");
            if (allDoneInSession) {
                timer.stop();
                await courseService.completeCourse(
                    course.idCourse,
                    moduleId,
                    timer.timeInSeconds,
                    newSessionValidated,
                    {}
                );
                onClose();
                return;
            }

            if (currentChapterIndex < chapters.length - 1) {
                setCurrentChapterIndex(prev => prev + 1);
            }
        } catch (err) {
            console.error('Failed to validate section:', err);
        } finally {
            setValidating(false);
        }
    };

    const confirmLastChapterValidation = () => {
        setShowLastChapterConfirm(false);
        doValidateSection();
    };

    const handleCompleteClick = () => {
        if (!sessionId || completing) return;
        const allValidated = chapters.every(ch => sessionValidatedSections[String(ch.idChapter)] === "1");
        if (!allValidated) {
            setShowCompleteWarning(true);
            return;
        }
        handleCompleteCourse();
    };

    const handleCompleteCourse = async () => {
        if (!sessionId || completing) return;
        setCompleting(true);
        try {
            timer.stop();
            await courseService.completeCourse(
                course.idCourse,
                moduleId,
                timer.timeInSeconds,
                sessionValidatedSections,
                {}
            );
            onClose();
        } catch (err) {
            console.error('Failed to complete course:', err);
        } finally {
            setCompleting(false);
        }
    };

    const handleClose = () => {
        timer.stop();
        audioManager.cleanup();
        onClose();
    };

    const scrollToTop = useCallback(() => {
        smoothScrollTo(scrollContainerRef.current, 0, 380);
    }, []);


    // Pills slider helpers
    const updatePillsScrollState = useCallback(() => {
        const el = pillsContainerRef.current;
        if (!el) return;
        setPillsCanScrollLeft(el.scrollLeft > 4);
        setPillsCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
    }, []);

    const scrollPills = useCallback((direction) => {
        const el = pillsContainerRef.current;
        if (!el) return;
        smoothScrollTo({ get scrollTop() { return el.scrollLeft; }, set scrollTop(v) { el.scrollLeft = v; } }, el.scrollLeft + direction * 150, 280);
    }, []);

    useEffect(() => {
        const el = pillsContainerRef.current;
        if (!el) return;
        updatePillsScrollState();
        el.addEventListener('scroll', updatePillsScrollState, { passive: true });
        const ro = new ResizeObserver(updatePillsScrollState);
        ro.observe(el);
        return () => {
            el.removeEventListener('scroll', updatePillsScrollState);
            ro.disconnect();
        };
    }, [updatePillsScrollState, chapters]);

    const handleNextChapter = () => {
        if (currentChapterIndex < chapters.length - 1) {
            setCurrentChapterIndex(prev => prev + 1);
            scrollToTop();
        }
    };

    const handlePreviousChapter = () => {
        if (currentChapterIndex > 0) {
            setCurrentChapterIndex(prev => prev - 1);
            scrollToTop();
        }
    };

    if (!course) return null;

    const getCourseTitle = (course) => {
        return language === 'fr'
            ? (course.libelle_fr || course.titreCourse || 'Sans titre')
            : (course.libelle_en || course.titreCourse || 'Untitled');
    };

    const type = (course.typeCourse || course.type || '').toUpperCase();
    const isQuiz = course.hasQuiz || type === 'QUIZ' || type === 'QUIZZ';
    const currentChapter = chapters[currentChapterIndex];
    // Validate button checks SESSION-level (not global) — allows re-validation in "Revoir" mode
    const isCurrentSectionValidatedInSession = currentChapter && sessionValidatedSections[String(currentChapter.idChapter)] === "1";

    // Session progress (what matters inside the modal)
    const sessionValidatedCount = Object.values(sessionValidatedSections).filter(v => v === "1").length;
    const sessionProgressPercent = chapters.length > 0 ? Math.round((sessionValidatedCount / chapters.length) * 100) : 0;

    // Loading screen
    if (loading) {
        return (
            <div className="fixed inset-0 bg-gray-50 dark:bg-black flex items-center justify-center text-gray-900 dark:text-white z-[100] transition-colors duration-500">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-gray-400">
                        {language === 'fr' ? 'Chargement du cours...' : 'Loading course...'}
                    </p>
                </div>
            </div>
        );
    }

    // Error screen
    if (error) {
        return (
            <div className="fixed inset-0 bg-gray-50 dark:bg-black flex flex-col items-center justify-center text-gray-900 dark:text-white p-6 text-center z-[100] transition-colors duration-500">
                <h2 className="text-2xl font-bold mb-4 text-red-500">
                    {language === 'fr' ? 'Erreur' : 'Error'}
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
                <button
                    onClick={handleClose}
                    className="px-6 py-2 bg-gray-200 dark:bg-white/20 rounded-xl hover:bg-gray-300 dark:hover:bg-white/30 transition font-medium"
                >
                    {language === 'fr' ? 'Fermer' : 'Close'}
                </button>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-gray-50 dark:bg-black text-gray-900 dark:text-white flex flex-col z-[100] transition-colors duration-500">
            {/* Pause Overlay */}
            {coursePaused && (
                <div className="absolute inset-0 z-[110] bg-black/60 backdrop-blur-sm flex items-center justify-center animate-in fade-in duration-300">
                    <div className="text-center">
                        <h2 className="text-3xl font-bold mb-6 text-white">{language === 'fr' ? 'Cours en pause' : 'Course paused'}</h2>
                        <button
                            onClick={handleResumeCourse}
                            className="px-10 py-3 bg-white text-black rounded-2xl font-bold text-lg hover:bg-gray-200 transition"
                        >
                            {language === 'fr' ? 'Reprendre' : 'Resume'}
                        </button>
                    </div>
                </div>
            )}

            {/* Floating top-right pills — desktop only (mobile uses the dedicated top bar below) */}
            <div data-help="course-controls" className="hidden md:flex fixed top-3 right-3 z-40 items-center gap-2">
                <div className="flex items-center gap-0.5 px-1 py-1 bg-white/95 dark:bg-gray-900/90 backdrop-blur-md border border-gray-200 dark:border-white/15 rounded-full shadow-lg shadow-black/15">
                    <button
                        onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                        className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 text-gray-700 dark:text-gray-100 transition"
                        title={theme === 'dark' ? (language === 'fr' ? 'Mode clair' : 'Light mode') : (language === 'fr' ? 'Mode sombre' : 'Dark mode')}
                    >
                        {theme === 'dark' ? (
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                        ) : (
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                            </svg>
                        )}
                    </button>
                    <div className="w-px h-4 bg-gray-300 dark:bg-white/20" />
                    <button
                        onClick={() => setLanguage(language === 'fr' ? 'en' : 'fr')}
                        title={language === 'fr' ? 'Switch to English' : 'Passer en français'}
                        className="px-2 py-1 text-[11px] font-bold font-mono text-gray-700 dark:text-gray-100 hover:opacity-80 transition"
                    >
                        {language === 'fr' ? 'EN' : 'FR'}
                    </button>
                </div>

                {audioManager.hasAudio && (
                    <button
                        onClick={() => setShowSettings(true)}
                        title={language === 'fr' ? 'Options audio' : 'Audio options'}
                        className="w-9 h-9 rounded-full bg-white/95 dark:bg-gray-900/90 backdrop-blur-md border border-gray-200 dark:border-white/15 hover:bg-white dark:hover:bg-gray-800 text-gray-700 dark:text-gray-100 shadow-lg shadow-black/15 transition flex items-center justify-center"
                    >
                        <Settings className="w-4 h-4" />
                    </button>
                )}

                <button
                    onClick={handleCompleteClick}
                    disabled={completing}
                    className="flex items-center gap-1.5 pl-3 pr-4 py-2 rounded-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white text-xs font-bold transition shadow-lg shadow-green-600/40 disabled:opacity-50"
                >
                    {completing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trophy className="w-3.5 h-3.5" />}
                    <span>{language === 'fr' ? 'Terminer' : 'Complete'}</span>
                </button>
            </div>

            {/* ───────── Mobile-only top bar: back · progress+timer · settings ───────── */}
            <div className="md:hidden flex items-center gap-3 px-4 pt-4 pb-3 flex-shrink-0">
                <button
                    onClick={handleClose}
                    className="w-10 h-10 rounded-full bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/20 text-gray-700 dark:text-white flex items-center justify-center transition flex-shrink-0"
                    aria-label="Back"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                </button>

                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-center gap-3 text-xs">
                        <span className="font-bold text-blue-600 dark:text-blue-400 tabular-nums">
                            {sessionProgressPercent}% {sessionValidatedCount}/{chapters.length}
                        </span>
                        <span className="text-gray-500 dark:text-gray-400 tabular-nums">{timer.formattedTime}</span>
                    </div>
                    <div className="h-1.5 bg-gray-200 dark:bg-white/10 rounded-full overflow-hidden mt-1.5">
                        <div
                            className="h-full bg-blue-500 rounded-full transition-all duration-500"
                            style={{ width: `${sessionProgressPercent}%` }}
                        />
                    </div>
                </div>

                <button
                    onClick={() => setShowSettings(true)}
                    className="w-10 h-10 rounded-full bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/20 text-gray-700 dark:text-white flex items-center justify-center transition flex-shrink-0"
                    aria-label="Options"
                >
                    <Settings className="w-5 h-5" />
                </button>
            </div>

            {/* Section Pills Navigation — MOBILE ONLY (desktop uses the sidebar) */}
            {chapters.length > 0 && (
                <div data-help="course-chapters" className="md:hidden flex-shrink-0 border-b border-gray-200 dark:border-white/10">
                    <div className="flex items-center px-2">
                        {pillsCanScrollLeft && (
                            <button
                                onClick={() => scrollPills(-1)}
                                className="flex-shrink-0 w-8 h-8 rounded-full bg-white dark:bg-gray-800 shadow-md border border-gray-200 dark:border-gray-700 flex items-center justify-center text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                        )}

                        <div
                            ref={pillsContainerRef}
                            className="flex-1 min-w-0 flex gap-2 py-3 px-3 overflow-x-auto scroll-smooth no-scrollbar"
                        >
                            {chapters.map((ch, idx) => {
                                const validated = isSectionValidated(ch.idChapter);
                                const validatedThisSession = sessionValidatedSections[String(ch.idChapter)] === "1";
                                const validatedPrevious = validated && !validatedThisSession;
                                const isCurrent = currentChapterIndex === idx;
                                const isBolded = ch.style === 'bold';

                                return (
                                    <button
                                        key={ch.idChapter || idx}
                                        onClick={() => { setCurrentChapterIndex(idx); scrollToTop(); }}
                                        className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                                            isBolded ? 'ring-2 ring-rose-700 dark:ring-rose-500' : ''
                                        } ${
                                            isCurrent
                                                ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30'
                                                : validatedThisSession
                                                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                                                    : validatedPrevious
                                                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                                        : 'bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/10'
                                        }`}
                                    >
                                        {validatedThisSession ? (
                                            <CheckCircle className="w-4 h-4" />
                                        ) : (
                                            <Circle className="w-4 h-4" />
                                        )}
                                        <span className="truncate max-w-[160px]">
                                            {getSectionTitle(ch)}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>

                        {pillsCanScrollRight && (
                            <button
                                onClick={() => scrollPills(1)}
                                className="flex-shrink-0 w-8 h-8 rounded-full bg-white dark:bg-gray-800 shadow-md border border-gray-200 dark:border-gray-700 flex items-center justify-center text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Content area with sidebar (desktop) + inline navigation arrows */}
            <div className="flex-1 flex min-h-0">
                {/* Desktop sidebar listing all sections */}
                <CourseSectionsSidebar
                    chapters={chapters}
                    currentChapterIndex={currentChapterIndex}
                    sessionValidatedSections={sessionValidatedSections}
                    validatedSections={validatedSections}
                    onSelectChapter={(idx) => { setCurrentChapterIndex(idx); scrollToTop(); }}
                    progressPercent={sessionProgressPercent}
                    sessionValidatedCount={sessionValidatedCount}
                    formattedTime={timer.formattedTime}
                    getSectionTitle={getSectionTitle}
                    courseTitle={getCourseTitle(course)}
                    isQuiz={isQuiz}
                    coursePaused={coursePaused}
                    onTogglePause={() => {
                        if (coursePaused) { handleResumeCourse(); }
                        else { setCoursePaused(true); timer.pause(); }
                    }}
                    open={sidebarOpen}
                    language={language}
                />

                {/* Back arrow — always visible (desktop), closes the course like in account modal */}
                <button
                    onClick={handleClose}
                    className="hidden md:flex absolute top-4 left-4 z-10 w-10 h-10 rounded-full bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/20 text-gray-700 dark:text-white items-center justify-center transition"
                    title={language === 'fr' ? 'Retour au tableau de bord' : 'Back to dashboard'}
                    aria-label="Back"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                </button>

                {/* Sidebar toggle — top-right edge of sidebar, slides next to the back arrow when collapsed */}
                <button
                    onClick={() => setSidebarOpen(v => !v)}
                    className={`hidden md:flex absolute top-5 z-10 w-8 h-8 rounded-xl bg-gray-100 dark:bg-white/10 border border-gray-200 dark:border-white/10 flex-col items-center justify-center gap-[4px] hover:bg-gray-200 dark:hover:bg-white/20 transition-all duration-300 ${sidebarOpen ? 'left-64' : 'left-16'}`}
                    title={sidebarOpen
                        ? (language === 'fr' ? 'Masquer le menu' : 'Hide menu')
                        : (language === 'fr' ? 'Afficher le menu' : 'Show menu')}
                    aria-label="Toggle sidebar"
                >
                    <span className="block w-3.5 h-[1.5px] bg-gray-500 dark:bg-gray-400 rounded-full" />
                    <span className="block w-3.5 h-[1.5px] bg-gray-500 dark:bg-gray-400 rounded-full" />
                    <span className="block w-3.5 h-[1.5px] bg-gray-500 dark:bg-gray-400 rounded-full" />
                </button>

                {/* Left navigation arrow */}
                <div className="hidden md:flex flex-shrink-0 items-center px-1 lg:px-2">
                    <button
                        onClick={handlePreviousChapter}
                        disabled={currentChapterIndex === 0}
                        className="w-9 h-9 lg:w-10 lg:h-10 rounded-full bg-white dark:bg-gray-800 flex items-center justify-center text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 transition disabled:opacity-0 disabled:cursor-auto shadow-md border border-gray-200 dark:border-gray-700"
                        aria-label="Previous Section"
                    >
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                </div>

                {/* Main Content */}
                <main ref={scrollContainerRef} data-scroll-container data-help="course-content" className="flex-1 min-w-0 overflow-y-auto custom-scrollbar">
                    <div className={`min-h-full flex flex-col items-start justify-start max-w-6xl mx-auto w-full py-10 md:py-16 ${audioManager.hasAudio ? 'pb-32' : ''}`}>
                    {/* Section Title */}
                    {currentChapter && (
                        <div className="w-full mb-8 animate-in slide-in-from-bottom-4 duration-500 text-center">
                            <h2 className="text-2xl md:text-4xl xl:text-5xl font-extrabold leading-tight text-blue-600">
                                {getSectionTitle(currentChapter)}
                            </h2>
                        </div>
                    )}

                    {/* Paragraphs Content */}
                    {paragraphsLoading && (
                        <div className="flex items-center justify-center py-16">
                            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                        </div>
                    )}

                    {!paragraphsLoading && paragraphs.length > 0 && (
                        <div className="w-full animate-in fade-in duration-500">
                            <div className="max-w-3xl md:max-w-5xl mx-auto w-full space-y-8">
                                {paragraphs.map((paragraph, index) => {
                                    const paragraphContent = language === 'fr'
                                        ? (paragraph.libelle_fr || '')
                                        : (paragraph.libelle_en || '');

                                    const mediaLang = language === 'fr' ? paragraph.media_fr : paragraph.media_en;
                                    const mediaAll = paragraph.media_all;

                                    const mediaFile = mediaLang || mediaAll;
                                    const mediaUrl = mediaFile
                                        ? `/img/application/paragraphs/paragraph_${paragraph.idParagraph}/${mediaFile}`
                                        : null;
                                    const mediaType = mediaFile ? detectMediaType(mediaFile) : null;

                                    const displayOpt = paragraph.displayOption || 'NORMAL';

                                    const hasAudioForParagraph = paragraph.audio && paragraph.audio[language];
                                    const paragraphAudioIndex = hasAudioForParagraph
                                        ? audioManager.getFirstIndexForParagraph(paragraph.idParagraph)
                                        : -1;

                                    // DisplayOption wrapper styles
                                    const displayWrapperClass = {
                                        CENTER: '',
                                        HIGHLIGHT: 'border-l-4 border-blue-500 dark:border-blue-400 bg-blue-50/60 dark:bg-blue-900/20 rounded-r-xl px-5 py-4',
                                        NOTE: 'border border-sky-200 dark:border-sky-700 bg-sky-50/50 dark:bg-sky-900/15 rounded-xl px-5 py-4',
                                        WARNING: 'border border-amber-300 dark:border-amber-600 bg-amber-50/60 dark:bg-amber-900/20 rounded-xl px-5 py-4',
                                    }[displayOpt] || '';

                                    const displayIcon = {
                                        HIGHLIGHT: <Sparkles size={20} className="text-blue-500 dark:text-blue-400 shrink-0 mt-1" />,
                                        NOTE: <Info size={20} className="text-sky-600 dark:text-sky-400 shrink-0 mt-1" />,
                                        WARNING: <AlertTriangle size={20} className="text-amber-600 dark:text-amber-400 shrink-0 mt-1" />,
                                    }[displayOpt] || null;

                                    const isCentered = displayOpt === 'CENTER';

                                    return (
                                        <div
                                            key={paragraph.idParagraph || index}
                                            className="space-y-6"
                                            data-paragraph-id={paragraph.idParagraph}
                                        >
                                            {paragraphContent && (
                                                <div className={displayWrapperClass}>
                                                    <div className={`flex items-start gap-3 ${isCentered ? 'justify-center' : ''}`}>
                                                        {displayIcon}
                                                        {hasAudioForParagraph && (
                                                            <AudioPlayButton
                                                                isActive={audioManager.isPlayingParagraph(paragraph.idParagraph)}
                                                                isPlaying={audioManager.isPlaying}
                                                                onClick={() => {
                                                                    if (audioManager.isPlayingParagraph(paragraph.idParagraph)) {
                                                                        audioManager.playPause();
                                                                    } else {
                                                                        audioManager.playByIndex(paragraphAudioIndex);
                                                                    }
                                                                }}
                                                            />
                                                        )}
                                                        <div
                                                            data-paragraph-text={paragraph.idParagraph}
                                                            className={`text-xl md:text-2xl leading-relaxed text-gray-700 dark:text-gray-300 flex-1 ${isCentered ? 'text-center' : ''}`}
                                                            dangerouslySetInnerHTML={{ __html: paragraphContent }}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                            {mediaUrl && mediaType === 'audio' && (
                                                <div className="my-6 max-w-xl mx-auto">
                                                    <audio
                                                        controls
                                                        preload="metadata"
                                                        className="w-full rounded-xl"
                                                        src={mediaUrl}
                                                    >
                                                        {language === 'fr' ? 'Votre navigateur ne supporte pas le lecteur audio.' : 'Your browser does not support the audio element.'}
                                                    </audio>
                                                </div>
                                            )}
                                            {mediaUrl && mediaType !== 'audio' && (
                                                <div className="my-6 text-center">
                                                    <img
                                                        src={mediaUrl}
                                                        alt={`Illustration ${index + 1}`}
                                                        className="rounded-2xl shadow-xl max-w-full h-auto inline-block"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {!paragraphsLoading && paragraphs.length === 0 && (
                        <div className="text-center py-16">
                            <p className="text-gray-500 dark:text-gray-400 text-lg">
                                {language === 'fr' ? 'Aucun contenu disponible pour cette section.' : 'No content available for this section.'}
                            </p>
                        </div>
                    )}

                    {/* Validate Button (inside content for scroll) */}
                    {currentChapter && sessionId && (
                        <div data-help="course-validate" className="w-full mt-12 pt-8 border-t border-gray-200 dark:border-white/10">
                            <button
                                onClick={isCurrentSectionValidatedInSession ? handleNextChapter : handleValidateSection}
                                disabled={validating || (isCurrentSectionValidatedInSession && currentChapterIndex >= chapters.length - 1)}
                                className={`w-full py-4 rounded-2xl font-bold text-lg transition flex items-center justify-center gap-3 ${
                                    isCurrentSectionValidatedInSession
                                        ? currentChapterIndex < chapters.length - 1
                                            ? 'bg-green-500 hover:bg-green-600 text-white shadow-xl shadow-green-500/30 cursor-pointer'
                                            : 'bg-green-500 text-white cursor-default opacity-70'
                                        : validating
                                            ? 'bg-blue-400 text-white cursor-wait'
                                            : 'bg-blue-600 hover:bg-blue-700 text-white shadow-xl shadow-blue-500/30'
                                }`}
                            >
                                {validating ? (
                                    <>
                                        <Loader2 className="w-6 h-6 animate-spin" />
                                        {language === 'fr' ? 'Validation...' : 'Validating...'}
                                    </>
                                ) : isCurrentSectionValidatedInSession ? (
                                    currentChapterIndex < chapters.length - 1 ? (
                                        <>
                                            <CheckCircle className="w-6 h-6" />
                                            {language === 'fr' ? 'Section suivante' : 'Next section'}
                                            <ChevronRight className="w-5 h-5" />
                                        </>
                                    ) : (
                                        <>
                                            <CheckCircle className="w-6 h-6" />
                                            {language === 'fr' ? 'Section validée' : 'Section validated'}
                                        </>
                                    )
                                ) : (
                                    <>
                                        <Circle className="w-6 h-6" />
                                        {language === 'fr' ? 'Valider et continuer' : 'Validate and continue'}
                                    </>
                                )}
                            </button>

                        </div>
                    )}
                </div>
                </main>

                {/* Right navigation arrow */}
                <div className="hidden md:flex flex-shrink-0 items-center px-1 lg:px-2">
                    <button
                        onClick={handleNextChapter}
                        disabled={currentChapterIndex === chapters.length - 1}
                        className="w-9 h-9 lg:w-10 lg:h-10 rounded-full bg-blue-600 dark:bg-blue-500 flex items-center justify-center text-white hover:bg-blue-700 dark:hover:bg-blue-600 transition disabled:opacity-0 disabled:cursor-auto shadow-md shadow-blue-500/30"
                        aria-label="Next Section"
                    >
                        <ChevronRight className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {/* Floating audio toolbar (desktop + mobile, auto-hides like a video player) */}
            {audioManager.hasAudio && (
                <CourseAudioBottomBar
                    isPlaying={audioManager.isPlaying}
                    onPlayPause={audioManager.playPause}
                    onPrevParagraph={audioManager.playPrevParagraph}
                    onNextParagraph={audioManager.playNextParagraph}
                    onSeekBackward={audioManager.seekBackward}
                    onSeekForward={audioManager.seekForward}
                    sidebarOpen={sidebarOpen}
                    language={language}
                />
            )}

            {/* Audio settings modal */}
            <CourseSettingsModal
                open={showSettings}
                onClose={() => setShowSettings(false)}
                hasAudio={audioManager.hasAudio}
                volume={audioManager.volume}
                onVolumeChange={audioManager.setVolume}
                playbackRate={audioManager.playbackRate}
                onPlaybackRateChange={audioManager.setPlaybackRate}
                autoNext={audioManager.autoNext}
                onToggleAutoNext={audioManager.toggleAutoNext}
                autoScroll={audioManager.autoScroll}
                onToggleAutoScroll={audioManager.toggleAutoScroll}
                language={language}
                onToggleLanguage={() => setLanguage(language === 'fr' ? 'en' : 'fr')}
                theme={theme}
                onToggleTheme={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                coursePaused={coursePaused}
                onTogglePause={() => {
                    if (coursePaused) { handleResumeCourse(); }
                    else { setCoursePaused(true); timer.pause(); }
                }}
                onComplete={handleCompleteClick}
                completing={completing}
            />

            {/* Confirmation modal: last chapter validation */}
            {showLastChapterConfirm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[110]" onClick={() => setShowLastChapterConfirm(false)}>
                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md mx-4 max-h-[85vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900/50">
                                <CheckCircle className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                                {language === 'fr' ? 'Dernière section' : 'Last section'}
                            </h3>
                        </div>
                        <p className="text-gray-600 dark:text-gray-300 mb-6">
                            {language === 'fr'
                                ? 'Cela terminera cette session de révision, êtes-vous certain de valider ?'
                                : 'This will complete this review session. Are you sure you want to validate?'}
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowLastChapterConfirm(false)}
                                className="flex-1 py-2.5 rounded-xl font-medium bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/20 transition"
                            >
                                {language === 'fr' ? 'Annuler' : 'Cancel'}
                            </button>
                            <button
                                onClick={confirmLastChapterValidation}
                                className="flex-1 py-2.5 rounded-xl font-bold bg-blue-600 text-white hover:bg-blue-700 transition"
                            >
                                {language === 'fr' ? 'Confirmer' : 'Confirm'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Warning modal: complete without all chapters */}
            {showCompleteWarning && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[110]" onClick={() => setShowCompleteWarning(false)}>
                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md mx-4 max-h-[85vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 rounded-full bg-amber-100 dark:bg-amber-900/50">
                                <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                                {language === 'fr' ? 'Sections non validées' : 'Unvalidated sections'}
                            </h3>
                        </div>
                        <p className="text-gray-600 dark:text-gray-300 mb-4">
                            {language === 'fr'
                                ? `Vous devez valider toutes les sections avant de terminer. Il reste ${chapters.length - sessionValidatedCount} section${(chapters.length - sessionValidatedCount) > 1 ? 's' : ''} sur ${chapters.length}.`
                                : `You must validate all sections before completing. ${chapters.length - sessionValidatedCount} out of ${chapters.length} section${(chapters.length - sessionValidatedCount) > 1 ? 's' : ''} remaining.`}
                        </p>
                        {/* List unvalidated sections */}
                        <div className="mb-6 max-h-40 overflow-y-auto space-y-1.5">
                            {chapters.filter(ch => sessionValidatedSections[String(ch.idChapter)] !== "1").map((ch, i) => (
                                <button
                                    key={ch.idChapter}
                                    onClick={() => {
                                        const idx = chapters.findIndex(c => c.idChapter === ch.idChapter);
                                        if (idx >= 0) {
                                            setCurrentChapterIndex(idx);
                                            scrollToTop();
                                        }
                                        setShowCompleteWarning(false);
                                    }}
                                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-500/10 text-left hover:bg-amber-100 dark:hover:bg-amber-500/20 transition"
                                >
                                    <Circle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                                    <span className="text-sm text-amber-700 dark:text-amber-300 truncate">
                                        {getSectionTitle(ch)}
                                    </span>
                                </button>
                            ))}
                        </div>
                        <button
                            onClick={() => {
                                const firstUnvalidated = chapters.findIndex(ch => sessionValidatedSections[String(ch.idChapter)] !== "1");
                                if (firstUnvalidated >= 0) {
                                    setCurrentChapterIndex(firstUnvalidated);
                                    scrollToTop();
                                }
                                setShowCompleteWarning(false);
                            }}
                            className="w-full py-2.5 rounded-xl font-bold bg-blue-600 text-white hover:bg-blue-700 transition"
                        >
                            {language === 'fr' ? 'Reprendre la révision' : 'Resume reviewing'}
                        </button>
                    </div>
                </div>
            )}

            {/* Paragraph audio element (hidden) */}
            <audio ref={audioManager.audioRef} data-audio-manager preload="none" />

            {/* Paragraph audio element is rendered above, toolbar is in pills section */}
        </div>
    );
};

export default CourseModal;
